import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const SuccessModal = ({ isOpen, onClose, title = "Demande envoyée !", message = "Votre demande a été transmise avec succès. Notre équipe vous contactera dans les plus brefs délais." }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) onClose();
    navigate('/');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-[#020617]/90 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl overflow-hidden"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#D4AF37]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-green-500/10 rounded-full blur-3xl" />

            {/* Content */}
            <div className="relative text-center">
              {/* Animated checkmark */}
              <motion.div
                className="relative mx-auto mb-6"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 15, stiffness: 300 }}
              >
                {/* Outer ring animation */}
                <motion.div
                  className="absolute inset-0 w-20 h-20 mx-auto rounded-full border-4 border-green-500/30"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1.2, opacity: [0, 1, 0] }}
                  transition={{ delay: 0.3, duration: 1, repeat: Infinity, repeatDelay: 2 }}
                />
                
                {/* Main circle with checkmark */}
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/30">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.4, type: "spring", damping: 15 }}
                  >
                    <CheckCircle className="w-10 h-10 text-white" strokeWidth={2.5} />
                  </motion.div>
                </div>

                {/* Sparkles */}
                <motion.div
                  className="absolute -top-2 -right-2"
                  initial={{ scale: 0, rotate: -45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.6, type: "spring" }}
                >
                  <Sparkles className="w-6 h-6 text-[#D4AF37]" />
                </motion.div>
                <motion.div
                  className="absolute -bottom-1 -left-3"
                  initial={{ scale: 0, rotate: 45 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.7, type: "spring" }}
                >
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                </motion.div>
              </motion.div>

              {/* Title */}
              <motion.h2
                className="text-2xl font-bold text-white mb-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {title}
              </motion.h2>

              {/* Message */}
              <motion.p
                className="text-[#94A3B8] text-sm leading-relaxed mb-8"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                {message}
              </motion.p>

              {/* Button */}
              <motion.button
                onClick={handleClose}
                className="w-full btn-gold py-3 text-sm font-semibold flex items-center justify-center gap-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="success-modal-close"
              >
                J'ai compris
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SuccessModal;
